import {
  AlwaysDepth,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial, // Box3,
  type BoxGeometry,
  type BufferGeometry,
} from 'three';

import { getOrCreateThreeColor } from '../colors';
import {
  SPLIT_CHAR,
  temporarilySimplifiedColorHex,
  tooComplexPartViewerColorHex,
} from '../constants';
import { disposeThreeJS } from '../geom-utils';
import type { Intersectable } from '../renderer/renderer';
import type { OptimalBoundingBoxGeometry } from './OptimalBoundingBoxGeometry';
import type { AssemblyInstance } from './assemblyInstance';
import type { AnyBody } from './body';

const defaultSimpRepColor = getOrCreateThreeColor(
  temporarilySimplifiedColorHex,
);
const tooComplexSimpRepColor = getOrCreateThreeColor(
  tooComplexPartViewerColorHex,
);

const simpRepMeshMaterial = new MeshBasicMaterial({
  color: defaultSimpRepColor,
  opacity: 0.35,
  transparent: true,
  depthFunc: AlwaysDepth,
  depthWrite: true,
});

const simpRepLineMaterial = new LineBasicMaterial({
  color: defaultSimpRepColor,
  opacity: 1,
});

const tooComplexMeshMaterial = new MeshBasicMaterial({
  color: tooComplexSimpRepColor,
  opacity: 0.35,
  transparent: true,
  depthFunc: AlwaysDepth,
  depthWrite: true,
});

const tooComplexLineMaterial = new LineBasicMaterial({
  color: tooComplexSimpRepColor,
  opacity: 1,
});

//OBBSimplifiedRepresentation is a simplified representation created from the Optimal Bounding Box's geometry
//* It contains a mesh and lines that will render with better performance than the fullDetailRep of a body or assemblyInstance
//  but with the trade off of not having the full detail of the fullDetailRep
export class OBBSimplifiedRepresentation {
  private readonly group = new Group();
  public readonly intersectable: Intersectable;
  private readonly mesh: Mesh<BoxGeometry, MeshBasicMaterial>;
  private readonly wireframe: LineSegments<BufferGeometry, LineBasicMaterial>;
  private isDisposed?: boolean;

  constructor(
    public readonly geometry: OptimalBoundingBoxGeometry,
    name: string,
    private readonly parent: AnyBody | AssemblyInstance,
  ) {
    this.group.name = name;
    this.mesh = new Mesh(geometry.box, simpRepMeshMaterial);
    this.mesh.name = [name, 'mesh'].join(SPLIT_CHAR);
    this.wireframe = new LineSegments(
      this.geometry.geometry,
      simpRepLineMaterial,
    );
    this.wireframe.name = [name, 'wireframe'].join(SPLIT_CHAR);
    this.group.add(this.mesh, this.wireframe);
    this.intersectable = this.mesh;
    parent.group.add(this.group);
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    disposeThreeJS(this.mesh, this.wireframe, this.group);
  }

  public get rootBodyOrAssemblyInstanceId() {
    return this.parent.rootBodyOrAssemblyInstanceId;
  }

  public setColorTooComplex() {
    this.mesh.material = tooComplexMeshMaterial;
    this.wireframe.material = tooComplexLineMaterial;
  }

  public setDefaultColor() {
    this.mesh.material.color.set(defaultSimpRepColor);
    this.wireframe.material.color.set(defaultSimpRepColor);
  }

  public get name() {
    return this.group.name;
  }

  public get visible(): boolean {
    //return this.group.visible;
    let { parent, visible } = this.group;
    while (visible && parent) {
      visible = parent.visible;
      parent = parent.parent;
    }
    return visible;
  }

  public set visible(v: boolean) {
    this.group.visible = v;
  }

  public get minSideArea() {
    return this.geometry.minSideArea;
  }

  public get maxSideArea() {
    return this.geometry.maxSideArea;
  }

  public get diagonal() {
    return this.geometry.diagonal;
  }

  public get volume() {
    return this.geometry.volume;
  }
}
